"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.retrievePactLendingPoolInfo = retrievePactLendingPoolInfo;
exports.retrieveTinymanLendingPoolInfo = retrieveTinymanLendingPoolInfo;
const math_lib_1 = require("../math-lib");
const utils_1 = require("../utils");
/**
 *
 * Returns information regarding the given Pact lending pool.
 *
 * @param client - Algorand client to query
 * @param lendingPool - Pact lending pool to query about
 * @param poolManagerInfo - pool manager info which is returned by retrievePoolManagerInfo function
 * @returns Promise<LendingPoolInfo> lending pool info
 */
function retrievePactLendingPoolInfo(client, lendingPool, poolManagerInfo) {
    return __awaiter(this, void 0, void 0, function* () {
        const { currentRound, globalState: state } = yield (0, utils_1.getApplicationGlobalState)(client, lendingPool.lpPoolAppId);
        if (state === undefined)
            throw Error("Could not find lending pool");
        const config = (0, utils_1.parseUint64s)(String((0, utils_1.getParsedValueFromState)(state, "CONFIG")));
        const fa0s = BigInt((0, utils_1.getParsedValueFromState)(state, "A") || 0);
        const fa1s = BigInt((0, utils_1.getParsedValueFromState)(state, "B") || 0);
        const ltcs = BigInt((0, utils_1.getParsedValueFromState)(state, "L") || 0);
        // pact pool swap fee interest
        const lpInfoRes = yield fetch(`https://api.pact.fi/api/pools/${lendingPool.lpPoolAppId}`);
        if (!lpInfoRes.ok || lpInfoRes.status !== 200)
            throw Error("Failed to fetch pact swap fee from api");
        const pactPoolData = yield lpInfoRes.json();
        const swapFeeInterestRate = BigInt(Math.round(Number((pactPoolData === null || pactPoolData === void 0 ? void 0 : pactPoolData["apr_7d"]) || 0) * 1e16));
        const tvlUsd = Number((pactPoolData === null || pactPoolData === void 0 ? void 0 : pactPoolData["tvl_usd"]) || 0);
        // lending pool deposit interest
        const pool0 = poolManagerInfo.pools[lendingPool.pool0AppId];
        const pool1 = poolManagerInfo.pools[lendingPool.pool1AppId];
        if (pool0 === undefined || pool1 === undefined)
            throw Error("Could not find deposit pool");
        return {
            currentRound,
            fAsset0Supply: fa0s,
            fAsset1Supply: fa1s,
            liquidityTokenCirculatingSupply: ltcs,
            fee: config[2],
            swapFeeInterestRate,
            swapFeeInterestYield: (0, math_lib_1.compoundEveryHour)(swapFeeInterestRate, math_lib_1.ONE_16_DP),
            asset0DepositInterestRate: pool0.depositInterestRate / BigInt(2),
            asset0DepositInterestYield: pool0.depositInterestYield / BigInt(2),
            asset1DepositInterestRate: pool1.depositInterestRate / BigInt(2),
            asset1DepositInterestYield: pool1.depositInterestYield / BigInt(2),
            tvlUsd,
        };
    });
}
/**
 *
 * Returns information regarding the given Tinyman lending pool.
 *
 * @param client - Algorand client to query
 * @param tinymanAppId - Tinyman application id where lending pool belongs to
 * @param lendingPool - Pact lending pool to query about
 * @param poolManagerInfo - pool manager info which is returned by retrievePoolManagerInfo function
 * @returns Promise<LendingPoolInfo> lending pool info
 */
function retrieveTinymanLendingPoolInfo(client, tinymanAppId, lendingPool, poolManagerInfo) {
    return __awaiter(this, void 0, void 0, function* () {
        const { currentRound, localState: state } = yield (0, utils_1.getAccountApplicationLocalState)(client, tinymanAppId, lendingPool.lpPoolAppAddress);
        if (state === undefined)
            throw Error("Could not find lending pool");
        const fee = BigInt((0, utils_1.getParsedValueFromState)(state, "total_fee_share") || 0);
        const fa0s = BigInt((0, utils_1.getParsedValueFromState)(state, "asset_2_reserves") || 0);
        const fa1s = BigInt((0, utils_1.getParsedValueFromState)(state, "asset_1_reserves") || 0);
        const ltcs = BigInt((0, utils_1.getParsedValueFromState)(state, "issued_pool_tokens") || 0);
        // pact pool swap fee interest
        const res = yield fetch(`https://mainnet.analytics.tinyman.org/api/v1/pools/${lendingPool.lpPoolAppAddress}`);
        if (!res.ok || res.status !== 200)
            throw Error("Failed to fetch tinyman swap fee from api");
        const tmPoolData = yield res.json();
        const swapFeeInterestRate = BigInt(Math.round(Number((tmPoolData === null || tmPoolData === void 0 ? void 0 : tmPoolData["annual_percentage_rate"]) || 0) * 1e16));
        const swapFeeInterestYield = BigInt(Math.round(Number((tmPoolData === null || tmPoolData === void 0 ? void 0 : tmPoolData["annual_percentage_yield"]) || 0) * 1e16));
        const farmInterestYield = BigInt(Math.round(Number((tmPoolData === null || tmPoolData === void 0 ? void 0 : tmPoolData["staking_total_annual_percentage_yield"]) || 0) * 1e16));
        const tvlUsd = Number((tmPoolData === null || tmPoolData === void 0 ? void 0 : tmPoolData["liquidity_in_usd"]) || 0);
        // lending pool deposit interest
        const pool0 = poolManagerInfo.pools[lendingPool.pool0AppId];
        const pool1 = poolManagerInfo.pools[lendingPool.pool1AppId];
        if (pool0 === undefined || pool1 === undefined)
            throw Error("Could not find deposit pool");
        return {
            currentRound,
            fAsset0Supply: fa0s,
            fAsset1Supply: fa1s,
            liquidityTokenCirculatingSupply: ltcs,
            fee,
            swapFeeInterestRate,
            swapFeeInterestYield,
            asset0DepositInterestRate: pool0.depositInterestRate / BigInt(2),
            asset0DepositInterestYield: pool0.depositInterestYield / BigInt(2),
            asset1DepositInterestRate: pool1.depositInterestRate / BigInt(2),
            asset1DepositInterestYield: pool1.depositInterestYield / BigInt(2),
            farmInterestYield,
            tvlUsd,
        };
    });
}
//# sourceMappingURL=amm.js.map